import type { MetricCard } from '../analytics/gateway.js';

/**
 * Screen 00's contract, which is ONE READ.
 *
 * The browser asks once and renders what came back. It does not ask Finance,
 * CRM and HR and then decide which to show: a client that receives all three
 * has already been sent data it may not have, and the hiding is a rendering
 * decision a devtools panel undoes. What this type cannot express is exactly
 * what the server does not send.
 */

export interface DashboardTask {
  readonly id: string;
  readonly title: string;
  readonly priority: string;
  readonly status: string;
  readonly dueAt: string | null;
  readonly sourceModule: string;
}

export interface DashboardApproval {
  readonly id: string;
  readonly instanceId: string;
  readonly stepName: string;
  readonly subjectResource: string;
  readonly dueAt: string | null;
}

export interface AttentionItem {
  readonly kind: 'task' | 'approval';
  readonly id: string;
  readonly title: string;
  readonly dueAt: string | null;
}

export interface ActivityItem {
  readonly id: string;
  readonly activityType: string;
  readonly resource: string;
  readonly recordId: string;
  readonly occurredAt: string;
}

export interface DashboardQuickAction {
  readonly key: string;
  readonly label: string;
  readonly path: string;
}

export interface ModuleSnapshot {
  readonly key: string;
  readonly moduleKey: string;
  readonly title: string;
  readonly cards: readonly MetricCard[];
}

export interface DashboardPeriod {
  readonly from: string;
  readonly to: string;
  /** The preceding window of equal length, which every delta is against. */
  readonly comparedTo: { readonly from: string; readonly to: string };
}

export interface Dashboard {
  /**
   * The window the server ACTUALLY READ, which is not always the one asked
   * for. The date control renders this, never its own state — a pill showing
   * the request rather than the answer lies about which month you are on.
   */
  readonly period: DashboardPeriod;
  /** Null when the caller may not read metrics. Never an empty card row. */
  readonly executiveSnapshot: {
    readonly key: string;
    readonly cards: readonly MetricCard[];
  } | null;
  readonly myWork: {
    readonly tasks: readonly DashboardTask[];
    readonly approvals: readonly DashboardApproval[];
    /** Distinguishes "you have none" from "you may not see them". */
    readonly tasksAvailable: boolean;
    readonly approvalsAvailable: boolean;
  };
  readonly needsAttention: {
    readonly overdueTasks: number;
    readonly overdueApprovals: number;
    readonly items: readonly AttentionItem[];
  } | null;
  readonly recentActivity: {
    /** `own_actions` — what this person did. Not a tenant feed. */
    readonly scope: 'own_actions';
    readonly items: readonly ActivityItem[];
  };
  readonly quickActions: readonly DashboardQuickAction[];
  readonly moduleSnapshots: readonly ModuleSnapshot[];
  readonly intelligence: { readonly key: string } | null;
  /** The caller's own unread count. Zero is a number; absence is not. */
  readonly notifications: { readonly unread: number };
}

export interface DashboardGateway {
  /** `period` is a request, not an instruction: the server may narrow it. */
  dashboard(period?: { readonly from: string; readonly to: string }): Promise<Dashboard>;
}

export function createDashboardGateway(baseUrl = '/api/v1'): DashboardGateway {
  return {
    async dashboard(period) {
      const query =
        period === undefined
          ? ''
          : `?${new URLSearchParams({ from: period.from, to: period.to }).toString()}`;
      const response = await fetch(`${baseUrl}/dashboard${query}`, {
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
      });
      if (!response.ok) {
        // The API's own wording, read rather than guessed. Which of 401, 403
        // and 404 this was is the server's decision and it is load-bearing.
        const problem = (await response.json().catch(() => ({}))) as {
          title?: string;
          detail?: string;
        };
        throw new Error(problem.detail ?? problem.title ?? `Request failed (${response.status})`);
      }
      return (await response.json()) as Dashboard;
    },
  };
}
